import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Plus, Download, Eye, Pencil, IdCard, MessageCircle, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { AdminHeader } from "../../components/admin/AdminLayout";
import { SituationBadge } from "../../components/admin/Badges";
import { MemberFormDialog } from "../../components/admin/MemberFormDialog";
import { MemberDetailDialog } from "../../components/admin/MemberDetailDialog";
import { MemberCardDialog } from "../../components/admin/MemberCard";
import { api, dateBR, downloadFile, formatError, whatsappChargeLink, whatsappReminderLink } from "../../lib/api";

function RowActions({ m, onView, onEdit, onCard, onStatus, onDelete }) {
  return (
    <div className="flex gap-1.5 justify-end">
      <Button size="sm" variant="outline" onClick={onView} data-testid={`view-${m.id}`} title="Detalhes"><Eye className="w-3.5 h-3.5" /></Button>
      <Button size="sm" variant="outline" onClick={onEdit} data-testid={`edit-${m.id}`} title="Editar"><Pencil className="w-3.5 h-3.5" /></Button>
      <Button size="sm" variant="outline" onClick={onCard} data-testid={`card-${m.id}`} title="Carteirinha"><IdCard className="w-3.5 h-3.5" /></Button>
      {m.days_until_due != null && m.days_until_due <= 3 && (
        <a href={whatsappReminderLink(m.nome_completo, m.telefone, m.next_due_date)} target="_blank" rel="noreferrer">
          <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800" data-testid={`whatsapp-remind-${m.id}`} title="Lembrar vencimento no WhatsApp">
            <MessageCircle className="w-3.5 h-3.5" />
          </Button>
        </a>
      )}
      {m.situation === "overdue" && (
        <a href={whatsappChargeLink(m.nome_completo, m.telefone, m.next_due_date, m.days_overdue)} target="_blank" rel="noreferrer">
          <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800" data-testid={`whatsapp-charge-${m.id}`} title="Cobrar no WhatsApp">
            <MessageCircle className="w-3.5 h-3.5" />
          </Button>
        </a>
      )}
      {m.status !== "active" && (
        <Button size="sm" className="bg-[#166534] hover:bg-[#14532d] hover:text-white text-white" onClick={() => onStatus("approve")} data-testid={`approve-${m.id}`}>Aprovar</Button>
      )}
      {m.status !== "blocked" && (
        <Button size="sm" variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800" onClick={() => onStatus("block")} data-testid={`block-${m.id}`}>Bloquear</Button>
      )}
      <Button size="sm" variant="outline" className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-800" onClick={onDelete} data-testid={`delete-${m.id}`} title="Excluir associado">
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

export default function Members() {
  const [members, setMembers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ open: false, member: null });
  const [detailId, setDetailId] = useState(null);
  const [card, setCard] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/members", {
        params: { status: filter === "all" ? undefined : filter, q: q || undefined },
      });
      setMembers(data.members);
    } catch (err) {
      toast.error(formatError(err));
    }
  }, [filter, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  async function deleteMember(m) {
    const ok = window.confirm(
      `Excluir definitivamente o associado ${m.cadastro_number} - ${m.nome_completo}?\n\nO cadastro e todo o histórico de pagamentos dele serão apagados. Esta ação não pode ser desfeita.`
    );
    if (!ok) return;
    try {
      await api.delete(`/admin/members/${m.id}`);
      toast.success("Associado excluído");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  }

  async function changeStatus(id, action) {
    try {
      await api.post(`/admin/members/${id}/${action}`);
      toast.success(action === "approve" ? "Associado aprovado" : "Associado bloqueado");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  }

  return (
    <div data-testid="admin-members-page">
      <AdminHeader title="Associados" subtitle={`${members.length} encontrado(s)`}>
        <Button variant="outline" onClick={() => downloadFile("/admin/export/members.csv", "associados.csv")} data-testid="export-members-csv">
          <Download className="w-4 h-4 mr-2" /> Exportar CSV
        </Button>
        <Button onClick={() => setForm({ open: true, member: null })} className="bg-[#166534] hover:bg-[#14532d] hover:text-white text-white" data-testid="new-member-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo associado
        </Button>
      </AdminHeader>

      <div className="rounded-2xl border border-neutral-200 bg-white">
        <div className="p-4 flex gap-3 items-center flex-wrap border-b border-neutral-100">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, e-mail, CPF ou nº de cadastro" className="pl-9" data-testid="admin-search" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48" data-testid="admin-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Ativos (em dia)</SelectItem>
              <SelectItem value="overdue">Vencidos</SelectItem>
              <SelectItem value="pending">Aguardando pagamento</SelectItem>
              <SelectItem value="blocked">Bloqueados</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-members-table">
            <thead className="text-left bg-neutral-50 text-neutral-600">
              <tr>
                {["Cadastro", "Nome", "Telefone", "Adesão", "Último pagamento", "Próximo vencimento", "Situação"].map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                ))}
                <th className="px-4 py-3 font-semibold text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-neutral-500">Nenhum associado encontrado.</td></tr>
              )}
              {members.map((m) => (
                <tr key={m.id} className="border-t border-neutral-100" data-testid={`member-row-${m.id}`}>
                  <td className="px-4 py-3 font-mono font-semibold text-[#166534]">{m.cadastro_number}</td>
                  <td className="px-4 py-3"><p className="font-medium">{m.nome_completo}</p><p className="text-xs text-neutral-500">{m.email}</p></td>
                  <td className="px-4 py-3 whitespace-nowrap">{m.telefone}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{dateBR(m.created_at)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{m.last_payment_at ? dateBR(m.last_payment_at) : "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-medium" data-testid={`next-due-${m.id}`}>{m.next_due_date ? dateBR(m.next_due_date) : "—"}</td>
                  <td className="px-4 py-3"><SituationBadge member={m} /></td>
                  <td className="px-4 py-3">
                    <RowActions
                      m={m}
                      onView={() => setDetailId(m.id)}
                      onEdit={() => setForm({ open: true, member: m })}
                      onCard={() => setCard(m)}
                      onStatus={(a) => changeStatus(m.id, a)}
                      onDelete={() => deleteMember(m)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <MemberFormDialog open={form.open} member={form.member} onClose={() => setForm({ open: false, member: null })} onSaved={load} />
      <MemberDetailDialog memberId={detailId} onClose={() => setDetailId(null)} onChanged={load} onCard={setCard} />
      <MemberCardDialog member={card} onClose={() => setCard(null)} />
    </div>
  );
}
